import { jsPDF } from "jspdf";

// Brand colors
const PRIMARY = [46, 52, 122];    // #2E347A
const ORANGE = [231, 103, 0];     // #E76700
const WHITE = [255, 255, 255];
const LIGHT_BG = [244, 245, 247]; // #F4F5F7
const DARK_TEXT = [33, 33, 33];
const GREY = [158, 158, 158];
const GREEN = [0, 180, 6];
const RED = [180, 0, 72];

function formatCurrency(value) {
  const num = Number(value) || 0;
  return "$" + num.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function drawHeader(doc, data, username) {
  // Top bar
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, 215, 36, "F");

  // Orange accent line
  doc.setFillColor(...ORANGE);
  doc.rect(0, 36, 215, 2, "F");

  // Title text
  doc.setTextColor(...WHITE);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("LC INTERNET", 14, 13);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("CORTE DE CAJA", 14, 21);

  // Generated by user
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("Generado por:", 14, 29);
  doc.setFont("helvetica", "normal");
  doc.text(username || "-", 40, 29);

  // Date on the right
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(data.fecha || "", 200, 13, { align: "right" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`${data.fechaGeneracion || ""}`, 200, 21, { align: "right" });

  // User icon badge on the right
  doc.setFontSize(7);
  doc.setFillColor(...ORANGE);
  const badgeText = (username || "").toUpperCase();
  const badgeW = doc.getTextWidth(badgeText) + 6;
  doc.roundedRect(200 - badgeW, 25, badgeW, 6, 1, 1, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.text(badgeText, 200 - badgeW / 2, 29, { align: "center" });

  return 44;
}

function drawSummaryCards(doc, resumen, y) {
  const cardW = 45;
  const cardH = 28;
  const gap = 5;
  const startX = 14;

  const cards = [
    { label: "TOTAL DEL DIA", value: formatCurrency(resumen.total), color: PRIMARY },
    { label: "PLAN HOGAR", value: formatCurrency(resumen.totalPlanHome), sub: `${resumen.cantidadPlanHome} pago(s)`, color: [33, 150, 243] },
    { label: "TEMPORALES", value: formatCurrency(resumen.totalTemporal), sub: `${resumen.cantidadTemporal} pago(s)`, color: [76, 175, 80] },
    { label: "RECIBOS", value: formatCurrency(resumen.totalReceipts), sub: `${resumen.cantidadRecibos} recibo(s)`, color: [255, 152, 0] },
  ];

  cards.forEach((card, i) => {
    const x = startX + i * (cardW + gap);

    // Card background
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, "F");

    // Colored top strip
    doc.setFillColor(...card.color);
    doc.roundedRect(x, y, cardW, 3, 2, 2, "F");
    doc.rect(x, y + 1.5, cardW, 1.5, "F");

    // Label
    doc.setTextColor(...GREY);
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "bold");
    doc.text(card.label, x + cardW / 2, y + 10, { align: "center" });

    // Value
    doc.setTextColor(...card.color);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(card.value, x + cardW / 2, y + 18, { align: "center" });

    // Sub text
    if (card.sub) {
      doc.setTextColor(...GREY);
      doc.setFontSize(6);
      doc.setFont("helvetica", "normal");
      doc.text(card.sub, x + cardW / 2, y + 24, { align: "center" });
    }
  });

  return y + cardH + 8;
}

function drawSectionTitle(doc, title, y, count) {
  doc.setFillColor(...PRIMARY);
  doc.roundedRect(14, y, 186, 7, 1, 1, "F");
  doc.setTextColor(...WHITE);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(title, 18, y + 5);
  if (count !== undefined) {
    doc.text(`${count}`, 196, y + 5, { align: "right" });
  }
  return y + 10;
}

function drawTable(doc, headers, rows, y, colWidths, colAligns) {
  const startX = 14;
  const rowH = 6;
  const pageH = doc.internal.pageSize.height;

  // Header row
  doc.setFillColor(...PRIMARY);
  doc.rect(startX, y, 186, rowH, "F");
  doc.setTextColor(...WHITE);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");

  let cx = startX;
  headers.forEach((h, i) => {
    const align = colAligns[i] || "left";
    const tx = align === "right" ? cx + colWidths[i] - 2 : align === "center" ? cx + colWidths[i] / 2 : cx + 2;
    doc.text(h, tx, y + 4, { align });
    cx += colWidths[i];
  });

  y += rowH;

  // Data rows
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "normal");

  rows.forEach((row, ri) => {
    if (y + rowH > pageH - 15) {
      doc.addPage();
      y = 15;
    }

    // Zebra striping
    if (ri % 2 === 0) {
      doc.setFillColor(...LIGHT_BG);
      doc.rect(startX, y, 186, rowH, "F");
    }

    doc.setTextColor(...DARK_TEXT);
    cx = startX;
    row.forEach((cell, i) => {
      const align = colAligns[i] || "left";
      const tx = align === "right" ? cx + colWidths[i] - 2 : align === "center" ? cx + colWidths[i] / 2 : cx + 2;

      // Color for currency difference columns
      if (cell.color) {
        doc.setTextColor(...cell.color);
        doc.setFont("helvetica", "bold");
        doc.text(cell.text, tx, y + 4, { align });
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...DARK_TEXT);
      } else {
        const text = typeof cell === "object" ? cell.text : String(cell);
        doc.text(text, tx, y + 4, { align });
      }

      cx += colWidths[i];
    });

    y += rowH;
  });

  return y + 3;
}

function drawTotalRow(doc, label, value, y) {
  const startX = 14;
  doc.setFillColor(...PRIMARY);
  doc.rect(startX, y, 186, 7, "F");
  doc.setTextColor(...WHITE);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(label, 18, y + 5);
  doc.text(value, 196, y + 5, { align: "right" });
  return y + 10;
}

function drawFooter(doc, username) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.height;

    // Footer line
    doc.setDrawColor(...ORANGE);
    doc.setLineWidth(0.5);
    doc.line(14, h - 12, 200, h - 12);

    doc.setTextColor(...GREY);
    doc.setFontSize(6);
    doc.setFont("helvetica", "normal");
    doc.text(`LC Internet - Generado por: ${username || "-"}`, 14, h - 8);
    doc.text(`Pagina ${i} de ${pageCount}`, 200, h - 8, { align: "right" });
  }
}

export function generateCorteCajaPDF(data, username) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const resumen = data.resumen || {};
  let y;

  // --- PAGE 1: Header + Summary + Plan Home ---
  y = drawHeader(doc, data, username);
  y = drawSummaryCards(doc, resumen, y);

  // Modifications alert banner
  if (resumen.cantidadModificaciones > 0) {
    doc.setFillColor(255, 235, 238);
    doc.roundedRect(14, y, 186, 7, 1, 1, "F");
    doc.setTextColor(...RED);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(`ALERTA: ${resumen.cantidadModificaciones} modificacion(es) registrada(s) en este corte`, 107, y + 5, { align: "center" });
    y += 10;
  }

  // --- Plan Hogar Table ---
  if (data.planHome && data.planHome.length > 0) {
    y = drawSectionTitle(doc, "PAGOS PLAN HOGAR", y, `${data.planHome.length} pago(s)`);

    const headers = ["#", "Codigo", "Cliente", "Estatus", "Monto"];
    const colWidths = [12, 25, 80, 35, 34];
    const colAligns = ["center", "center", "left", "center", "right"];

    const rows = data.planHome.map((item, i) => [
      String(i + 1),
      item.id || "",
      item.cliente || "",
      item.estatus || "",
      formatCurrency(item.monto),
    ]);

    y = drawTable(doc, headers, rows, y, colWidths, colAligns);
    y = drawTotalRow(doc, `Total Plan Hogar (${data.planHome.length})`, formatCurrency(resumen.totalPlanHome), y);
  }

  // --- Temporal Users Table ---
  if (data.temporal && data.temporal.length > 0) {
    if (y > 230) { doc.addPage(); y = 15; }
    y = drawSectionTitle(doc, "PAGOS TEMPORALES", y, `${data.temporal.length} pago(s)`);

    const headers = ["#", "Codigo", "Cliente", "Estatus", "Monto"];
    const colWidths = [12, 25, 80, 35, 34];
    const colAligns = ["center", "center", "left", "center", "right"];

    const rows = data.temporal.map((item, i) => [
      String(i + 1),
      item.id || "",
      item.cliente || "",
      item.estatus || "",
      formatCurrency(item.monto),
    ]);

    y = drawTable(doc, headers, rows, y, colWidths, colAligns);
    y = drawTotalRow(doc, `Total Temporales (${data.temporal.length})`, formatCurrency(resumen.totalTemporal), y);
  }

  // --- Recibos Table ---
  if (data.recibos && data.recibos.length > 0) {
    if (y > 230) { doc.addPage(); y = 15; }
    y = drawSectionTitle(doc, "RECIBOS", y, `${data.recibos.length} recibo(s)`);

    const headers = ["#", "Codigo", "Concepto", "Monto"];
    const colWidths = [12, 30, 110, 34];
    const colAligns = ["center", "center", "left", "right"];

    const rows = data.recibos.map((item, i) => [
      String(i + 1),
      item.id || "",
      item.concepto || "",
      formatCurrency(item.monto),
    ]);

    y = drawTable(doc, headers, rows, y, colWidths, colAligns);
    y = drawTotalRow(doc, `Total Recibos (${data.recibos.length})`, formatCurrency(resumen.totalReceipts), y);
  }

  // --- Modificaciones Table ---
  if (data.modificaciones && data.modificaciones.length > 0) {
    if (y > 200) { doc.addPage(); y = 15; }
    y = drawSectionTitle(doc, "MODIFICACIONES DEL DIA", y, `${data.modificaciones.length} cambio(s)`);

    const headers = ["Hora", "Usuario", "Accion", "Tipo", "Cliente", "Anterior", "Nuevo", "Diferencia"];
    const colWidths = [18, 24, 20, 24, 34, 22, 22, 22];
    const colAligns = ["center", "left", "center", "left", "left", "right", "right", "right"];

    const rows = data.modificaciones.map((mod) => {
      const anterior = Number(mod.montoAnterior) || 0;
      const nuevo = Number(mod.montoNuevo) || 0;
      const diff = Number(mod.diferencia) || (nuevo - anterior);
      const diffColor = diff > 0 ? GREEN : diff < 0 ? RED : DARK_TEXT;

      return [
        mod.hora || "",
        mod.usuario || "",
        mod.accion === "UPDATE" ? "Modifico" : mod.accion === "CREATE" ? "Creo" : mod.accion === "DELETE" ? "Elimino" : mod.accion || "",
        mod.tipo || "",
        mod.concepto || "",
        formatCurrency(anterior),
        formatCurrency(nuevo),
        { text: formatCurrency(diff), color: diffColor },
      ];
    });

    y = drawTable(doc, headers, rows, y, colWidths, colAligns);
  }

  // Footer on all pages
  drawFooter(doc, username);

  // Save
  const fileName = `corte-caja-${data.fecha ? data.fecha.replace(/\//g, "-") : "reporte"}.pdf`;
  doc.save(fileName);
}
